import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, List, Tag, Button, Spin, Alert } from 'antd';
import { ClockCircleOutlined, CalendarOutlined, HeartOutlined, MonitorOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiMethods, IslamicEvent, Zikr } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

function formatHijriDate(hijriDate?: string) {
  if (!hijriDate) return 'TBD';
  const [month, day] = hijriDate.split('-');
  const hijriMonthsArabic = [
    "محرم", "صفر", "ربيع الأول", "ربيع الثاني",
    "جمادى الأولى", "جمادى الثانية", "رجب", "شعبان",
    "رمضان", "شوال", "ذو القعدة", "ذو الحجة"
  ];
  const m = parseInt(month, 10);
  const d = parseInt(day, 10);
  if (!m || !d) return hijriDate;
  // Return Arabic month name, then day, with RTL direction
  return (
    <span style={{ direction: 'rtl', unicodeBidi: 'isolate' }}>
      {d} {hijriMonthsArabic[m - 1]}
    </span>
  );
}

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [upcomingEvents, setUpcomingEvents] = useState<IslamicEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  
  const [dailyZikr, setDailyZikr] = useState<Zikr | null>(null);
  const [zikrLoading, setZikrLoading] = useState(true);
  const [zikrError, setZikrError] = useState<string | null>(null);

    useEffect(() => {
    const fetchUpcomingEvents = async () => {
      try {
        setEventsLoading(true);
        setEventsError(null);
        const events = await apiMethods.getUpcomingEvents(4); // Get 4 upcoming events
        setUpcomingEvents(events);
      } catch (error) {
        console.error('Error fetching upcoming events:', error);
        setEventsError('Failed to load upcoming events');
      } finally {
        setEventsLoading(false);
      }
    };

    const fetchDailyZikr = async () => {
      try {
        setZikrLoading(true);
        setZikrError(null);
        const allZikr = await apiMethods.getZikr(); // Get all zikr
        if (allZikr.length > 0) {
          // Select a random zikr for daily display
          const randomIndex = Math.floor(Math.random() * allZikr.length);
          setDailyZikr(allZikr[randomIndex]);
        }
      } catch (error) {
        console.error('Error fetching daily zikr:', error);
        setZikrError('Failed to load daily zikr');
      } finally {
        setZikrLoading(false);
      }
    };

    fetchUpcomingEvents();
    fetchDailyZikr();
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Title level={2}>Welcome to Solumen App</Title>
      

      {/* Featured TV Preview Section */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              color: 'white'
            }}
            bodyStyle={{ color: 'white' }}
          >
            <Row align="middle" gutter={24}>
              <Col span={18}>
                <Title level={3} style={{ color: 'white', marginBottom: '8px' }}>
                  <MonitorOutlined style={{ marginRight: '8px' }} />
                  TV App Preview
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', display: 'block', marginBottom: '16px' }}>
                  See how your changes will appear on the TV app in real-time! Preview prayer times, events, media, and zikr exactly as they'll be displayed.
                </Text>
                <List
                  size="small"
                  dataSource={[
                    'Live preview of prayer times and current time',
                    'Real-time media carousel simulation',
                    'Events display with Islamic calendar integration',
                    'Zikr overlay demonstrations'
                  ]}
                  renderItem={item => (
                    <List.Item style={{ border: 'none', padding: '2px 0' }}>
                      <Text style={{ color: 'rgba(255,255,255,0.8)' }}>✓ {item}</Text>
                    </List.Item>
                  )}
                />
              </Col>
              <Col span={6} style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  onClick={() => navigate('/tv-preview')}
                  style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    height: '48px',
                    fontSize: '16px'
                  }}
                >
                  Launch Preview
                </Button>
                <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                  No TV app reload needed!
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} md={8}>
          <Card
            title={<><ClockCircleOutlined /> Prayer Times</>}
            extra={<a onClick={() => navigate('/prayer-times')}>Manage</a>}
          >
            <p>View and manage prayer times for different locations.</p>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={<><CalendarOutlined /> Upcoming Events</>}
            extra={<a onClick={() => navigate('/events')}>View All</a>}
          >
            {eventsLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="small" />
                <div style={{ marginTop: '8px' }}>Loading events...</div>
              </div>
            ) : eventsError ? (
              <Alert 
                message={eventsError} 
                type="error" 
                showIcon 
              />
            ) : upcomingEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                No upcoming events found
              </div>
            ) : (
              <List
                size="small"
                dataSource={upcomingEvents}
                renderItem={event => (
                  <List.Item>
                    <div>
                      {event.title}
                      <Tag
                        color={event.type === 'holiday' ? 'green' : event.type === 'special_day' ? 'blue' : 'orange'}
                        style={{ marginLeft: 8 }}
                      >
                        {event.greg_date
                          ? dayjs(event.greg_date).format('MMM DD')
                          : event.hijri_date
                            ? formatHijriDate(event.hijri_date)
                            : 'TBD'}
                      </Tag>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={<><HeartOutlined /> Daily Zikr</>}
            extra={<a onClick={() => navigate('/zikr')}>View All</a>}
          >
            {zikrLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="small" />
                <div style={{ marginTop: '8px' }}>Loading zikr...</div>
              </div>
            ) : zikrError ? (
              <Alert 
                message={zikrError} 
                type="error" 
                showIcon 
              />
            ) : dailyZikr ? (
              <div>
                <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '16px' }}>
                  {dailyZikr.arabic}
                </div>
                <div style={{ marginBottom: '8px', fontStyle: 'italic', color: '#666' }}>
                  {dailyZikr.transliteration}
                </div>
                <div style={{ fontSize: '14px', color: '#888' }}>
                  {dailyZikr.translation}
                </div>
                {dailyZikr.count && (
                  <Tag color="blue" style={{ marginTop: '8px' }}>
                    Repeat {dailyZikr.count}x
                  </Tag>
                )}
              </div>
            ) : (
              <p>View and manage your daily remembrance collection.</p>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card>
            <Title level={4}>About Prayer Times App</Title>
            <Text>
              This application helps you keep track of daily prayer times, important Islamic events,
              and maintains a collection of daily remembrance (zikr). You can:
            </Text>
            <List style={{ marginTop: 16 }}>
              <List.Item>• View and manage prayer times for different locations</List.Item>
              <List.Item>• Stay updated with important Islamic events and holidays</List.Item>
              <List.Item>• Access daily zikr and remembrance guides</List.Item>
              <List.Item>• Upload and manage prayer time schedules</List.Item>
              <List.Item>• Preview changes in real-time with the TV app simulator</List.Item>
            </List>
          </Card>
        </Col>
      </Row>
    </div>
  );
}; 